import React, { useState } from 'react';

const knightMoves = [
  [2, 1], [1, 2], [-1, 2], [-2, 1],
  [-2, -1], [-1, -2], [1, -2], [2, -1]
];

function isValid(x, y, board) {
  return x >= 0 && x < 5 && y >= 0 && y < 5 && board[y][x] === -1;
}

function knightTour(x, y, moveIndex, board, path) {
  if (moveIndex === 25) {
    return true;
  }

  for (const [dx, dy] of knightMoves) {
    const nextX = x + dx;
    const nextY = y + dy;
    
    if (isValid(nextX, nextY, board)) {
      board[nextY][nextX] = moveIndex;
      path.push([nextX, nextY]);
      
      if (knightTour(nextX, nextY, moveIndex + 1, board, path)) {
        return true;
      }
      
      // Backtrack
      board[nextY][nextX] = -1;
      path.pop();
    }
  }
  
  return false;
}

function generateTour() {
  const board = Array(5).fill(null).map(() => Array(5).fill(-1));
  const path = [];
  
  // Random starting point
  const startX = Math.floor(Math.random() * 5);
  const startY = Math.floor(Math.random() * 5);
  
  board[startY][startX] = 0;
  path.push([startX, startY]);
  
  if (knightTour(startX, startY, 1, board, path)) {
    return { board, path };
  } else {
    return generateTour(); // Try again if failed
  }
}

function boardToKnightTourArray(board) {
  const knightTour = [];
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 5; x++) {
      knightTour.push(board[y][x]);
    }
  }
  return knightTour;
}

function formatKnightTourForCode(knightTour) {
  let formatted = 'knightTour: [\n';
  for (let i = 0; i < 5; i++) {
    const row = knightTour.slice(i * 5, (i + 1) * 5);
    formatted += '  ' + row.join(', ');
    if (i < 4) formatted += ',';
    formatted += '\n';
  }
  formatted += ']';
  return formatted;
}

export default function KnightTourGenerator() {
  const [tour, setTour] = useState(null);
  const [copied, setCopied] = useState(false);

  // Override any parent overflow styles
  React.useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const generateNewTour = () => {
    const result = generateTour();
    setTour(result);
    setCopied(false);
  };

  const copyToClipboard = () => {
    if (tour) {
      const knightTourArray = boardToKnightTourArray(tour.board);
      const formatted = formatKnightTourForCode(knightTourArray);
      navigator.clipboard.writeText(formatted);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ 
      padding: '24px', 
      fontFamily: 'system-ui, sans-serif', 
      maxWidth: '800px', 
      margin: '0 auto', 
      minHeight: '100vh',
      overflow: 'auto',
      height: 'auto'
    }}>
      <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>Knight's Tour Generator</h1>
      <p style={{ color: '#666', marginBottom: '24px' }}>
        Generate random valid knight's tour patterns for your puzzle game
      </p>

      <button
        onClick={generateNewTour}
        style={{
          padding: '12px 24px',
          backgroundColor: '#541930',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '500',
          cursor: 'pointer',
          marginBottom: '24px'
        }}
      >
        Generate New Tour
      </button>

      {tour && (
        <div>
          {/* Visual Grid */}
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>Visual Representation</h2>
            <svg width="400" height="400" style={{ border: '2px solid #ddd', borderRadius: '8px' }}>
              {/* Grid lines */}
              {[0, 1, 2, 3, 4, 5].map(i => (
                <React.Fragment key={`grid-${i}`}>
                  <line
                    x1={i * 80}
                    y1={0}
                    x2={i * 80}
                    y2={400}
                    stroke="#ddd"
                    strokeWidth="1"
                  />
                  <line
                    x1={0}
                    y1={i * 80}
                    x2={400}
                    y2={i * 80}
                    stroke="#ddd"
                    strokeWidth="1"
                  />
                </React.Fragment>
              ))}
              
              {/* Path lines */}
              {tour.path.map((pos, i) => {
                if (i === 0) return null;
                const [x0, y0] = tour.path[i - 1];
                const [x1, y1] = pos;
                return (
                  <line
                    key={`line-${i}`}
                    x1={x0 * 80 + 40}
                    y1={y0 * 80 + 40}
                    x2={x1 * 80 + 40}
                    y2={y1 * 80 + 40}
                    stroke="#6310e0"
                    strokeWidth="3"
                    opacity="0.5"
                  />
                );
              })}
              
              {/* Numbers */}
              {tour.path.map((pos, i) => {
                const [x, y] = pos;
                return (
                  <g key={`num-${i}`}>
                    <circle
                      cx={x * 80 + 40}
                      cy={y * 80 + 40}
                      r="20"
                      fill="#e0106e"
                    />
                    <text
                      x={x * 80 + 40}
                      y={y * 80 + 40}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="white"
                      fontSize="16"
                      fontWeight="bold"
                    >
                      {i}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Knight Tour Array */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '18px', margin: 0 }}>Knight Tour Array</h2>
              <button
                onClick={copyToClipboard}
                style={{
                  padding: '8px 16px',
                  backgroundColor: copied ? '#10b981' : '#541930',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                {copied ? '✓ Copied!' : 'Copy Code'}
              </button>
            </div>
            <pre style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}>
              {formatKnightTourForCode(boardToKnightTourArray(tour.board))}
            </pre>
          </div>

          {/* Grid View */}
          <div>
            <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>Grid View (Step Numbers)</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 60px)',
              gap: '8px',
              width: 'fit-content'
            }}>
              {tour.board.map((row, y) =>
                row.map((cell, x) => (
                  <div
                    key={`${y}-${x}`}
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#541930',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      borderRadius: '8px'
                    }}
                  >
                    {cell}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}